import { noop } from '@sgme/fp';
import type { GridApi } from 'ag-grid-community';
import { type GridReadyEvent, type ModelUpdatedEvent, type SortChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { GridSummary } from 'components/Blotters/Filter/Badges/GridSummary';
import { useClientSideGridSummary, useHeaderTranslationById } from 'components/Blotters/Filter/Badges/hooks';
import { GridWrapperWithTheme } from 'components/shared/Blotter/Grid/grid-wrapper-with-theme';
import { useClientSideDisableColumnOptions } from 'components/shared/Blotter/Grid/Hooks/useDisableColumnFilter';
import { useResetColumns } from 'components/shared/Blotter/Grid/Hooks/useResetColumns';
import { useResetFilter } from 'components/shared/Blotter/Grid/Hooks/useResetFilter';
import 'configuration/ag-grid/ag-grid.init';
import { useAgGridIntl } from 'hooks/ag-grid/useAgGridIntl';
import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GenericErrorBoundary } from '../../../common-form/builder/form/GenericErrorBoundary';
import { canSetActive, getHeaderForBlotterIntlKey } from '../utils';
import type { BlotterGridProps } from './blotter.model';
import { useErrorBoundaryWrapper } from './Hooks/useErrorBoundaryWrapper';
import { useOnCellClickHandler } from './Hooks/useOnCellClickHandler';
import { useApplyColumnSettings, usePersistColumnState } from './Hooks/usePersistColumnState';

const emptyArray: readonly [] = [];
const rowSelection = { mode: 'singleRow', checkboxes: false, enableClickSelection: true } as const;

export function ClientSideBlotterGrid<Item>({
  items,
  pageSizeState,
  columnState = emptyArray,
  columnGroupState,
  onBlotterColumnStateChanged = () => undefined,
  onColumnSortChanged,
  columnDefinitions,
  onSetActiveItem,
  e2e,
  onGridReady = noop,
  onDisplayItemsCntChanged = noop,
  page,
  columnTypes,
  modules,
  name,
  logAnalyticName,
  pagination = true,
  children,
  ...gridOptions
}: PropsWithChildren<BlotterGridProps<Item>>) {
  const getLocaleText = useAgGridIntl();
  const [gridApi, setGridApi] = useState<GridApi>();
  const colDefs = useClientSideDisableColumnOptions<Item>(columnDefinitions);
  const onColumnChanged = usePersistColumnState(onBlotterColumnStateChanged, gridOptions.maintainColumnOrder);

  useApplyColumnSettings(gridApi, columnState, columnGroupState, colDefs);

  const onAgGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api);
      onGridReady(event);
    },
    [onGridReady],
  );

  const onSortChanged = useCallback(
    (event: SortChangedEvent) => {
      const { api } = event;

      if (onColumnSortChanged !== undefined) {
        const columns = api.getAllGridColumns();
        const col = columns.find(col => col.getSort() === 'desc' || col.getSort() === 'asc');
        const sort = col?.getColId();
        const sortDir = col?.getSort();

        onColumnSortChanged(sort ?? null, sortDir ?? null);
      }
      onColumnChanged(event);
    },
    [onColumnChanged, onColumnSortChanged],
  );

  const gridErrorContext = { grid: e2e };
  const wrapper = useErrorBoundaryWrapper<Item>(gridErrorContext);
  const onCellClickedHandler = useOnCellClickHandler<Item>(onSetActiveItem, canSetActive);

  const itemsCountRef = useRef(0);
  const displayedItemCountChanged = useCallback(
    (e: ModelUpdatedEvent<Item>) => {
      const itemsCount = e.api.getDisplayedRowCount();
      if (itemsCount !== itemsCountRef.current) {
        onDisplayItemsCntChanged(itemsCount);
        itemsCountRef.current = itemsCount;
      }
    },
    [onDisplayItemsCntChanged],
  );

  useEffect(() => {
    if (page !== undefined) {
      gridApi?.paginationGoToPage(page);
    }
  }, [page, gridApi]);
  const translationFunc = useMemo(() => getHeaderForBlotterIntlKey(name), [name]);
  const resetFilter = useResetFilter(gridApi);
  const resetColumns = useResetColumns(gridApi, logAnalyticName);
  const badgeTranslationFunc = useHeaderTranslationById(translationFunc, columnDefinitions);
  const [filters, sortCols] = useClientSideGridSummary(columnState);

  return (
    <GenericErrorBoundary context={gridErrorContext}>
      <GenericErrorBoundary>
        <div className="d-flex align-items-center justify-content-between" data-e2e="filters-badges">
          <GridSummary
            filters={filters}
            sortCols={sortCols}
            translationFunc={badgeTranslationFunc}
            onDismiss={resetFilter}
            onReset={resetColumns}
          />
          {children}
        </div>
      </GenericErrorBoundary>

      <GridWrapperWithTheme e2e={e2e} condensed={false}>
        <GenericErrorBoundary>
          <AgGridReact<Item>
            cacheQuickFilter
            cellSelection
            onCellClicked={onCellClickedHandler}
            columnDefs={colDefs}
            paginationPageSize={pageSizeState}
            rowData={items as any[]}
            animateRows
            onColumnPinned={onColumnChanged}
            onColumnVisible={onColumnChanged}
            onColumnMoved={onColumnChanged}
            onSortChanged={onSortChanged}
            onColumnResized={onColumnChanged}
            onFilterChanged={onColumnChanged}
            onColumnGroupOpened={onColumnChanged}
            columnTypes={wrapper(columnTypes)}
            onModelUpdated={displayedItemCountChanged}
            pagination={pagination}
            suppressPaginationPanel
            onGridReady={onAgGridReady}
            rowSelection={rowSelection}
            sideBar={false}
            getLocaleText={getLocaleText}
            modules={modules}
            rowHeight={48}
            headerHeight={56}
            {...gridOptions}
          />
        </GenericErrorBoundary>
      </GridWrapperWithTheme>
    </GenericErrorBoundary>
  );
}
